import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { updateWatchedFieldsUtils } from "src/app/features/Site/SiteAndSiteGroupUtils";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useSiteGroupLoadQuery, useSiteGroupUpdateMutation } from "../../services/server/SiteGroupApi";
import type { SiteGroupEntity } from "../../types/SiteGroupEntity";

export const SiteGroupEditForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("site-group");

    const [formData, setFormData] = useState<SiteGroupEntity>({} as SiteGroupEntity);
    const navigate = useNavigate();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useSiteGroupUpdateMutation();
    const dispatch = useDispatch();
    const { id } = useParams();

    // HANDLE INITIAL VALUES
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useSiteGroupLoadQuery({ entityId: id });

    const [isNewParentUnitIdChanged, setIsNewParentUnitIdChanged] = useState<boolean>(false);

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: SiteGroupEntity) => {
            if (newData.parentUnit !== formData?.parentUnit) {
                setIsNewParentUnitIdChanged(true);
            }
            setFormData({
                ...newData,
                dynamicPropertiesCheckReadOnly: newData?.parentUnit === undefined ? false : formData?.dynamicPropertiesCheckReadOnly,
            });
        },
        [setFormData, setIsNewParentUnitIdChanged, formData],
    );

    // HANDLE WATCH FIELDS
    const { data: siteGroupEntity } = useSiteGroupLoadQuery(
        { entityId: isNewParentUnitIdChanged ? `${formData.parentUnit}` : undefined },
        { skip: !isNewParentUnitIdChanged || !formData.parentUnit },
    );

    const updateWatchedFields = useCallback(() => {
        if (!siteGroupEntity || !setIsNewParentUnitIdChanged) return null;
        if (formData.parentUnit === undefined) return null;
        return updateWatchedFieldsUtils(siteGroupEntity, formData);
    }, [siteGroupEntity, formData, setIsNewParentUnitIdChanged]);

    useEffect(() => {
        if (!isNewParentUnitIdChanged || !siteGroupEntity) {
            return;
        }
        const newFormData = updateWatchedFields();
        if (newFormData && !isTheSameByDeepCompare(formData, newFormData)) {
            setIsNewParentUnitIdChanged(false);
            setFormData(newFormData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, updateWatchedFields, siteGroupEntity]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "Site Group",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.SITE_GROUPS}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<SiteGroupEntity>
                    name="SiteGroupEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
